/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Custom hook for debouncing a callback function.
 *
 * @param callback - The function to debounce.
 * @param delay - The duration in milliseconds to debounce the callback.
 * @returns A debounced version of the callback.
 */
export const useDebounce = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  const timerId = useRef<NodeJS.Timeout | null>(null);
  const fnRef = useRef<T>(fn);

  // Update the function reference whenever the function changes
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  // Create the debounced function
  const debounced = useCallback(
    (...args: Parameters<T>): Promise<ReturnType<T>> => {
      if (timerId.current) {
        clearTimeout(timerId.current);
      }

      return new Promise((resolve, reject) => {
        timerId.current = setTimeout(() => {
          fnRef
            .current(...args)
            .then(resolve)
            .catch(reject);
        }, delay);
      });
    },
    [delay] // Only depend on delay
  );

  return debounced;
};

/**
 * Custom hook for throttling a callback function.
 *
 * @param callback - The function to throttle.
 * @param delay - The duration in milliseconds to throttle the callback.
 * @returns A throttled version of the callback.
 */
export const useThrottle = <T extends (...args: any[]) => Promise<any> | void>(
  callback: T,
  delay: number,
  ignoreThrottle: boolean
) => {
  const lastRan = useRef<number>(0); // Last time the callback was invoked
  const callbackRef = useRef<T>(callback); // Store the callback reference

  // Update the callback ref whenever the callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create the throttled function
  const throttledCallback = useCallback(
    async (...args: Parameters<T>) => {
      const now = Date.now();
      // Check if the delay has passed
      if (now - lastRan.current >= delay) {
        lastRan.current = now; // Update last run time
        return await Promise.resolve(callbackRef.current(...args)); // Call the current callback
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay, ignoreThrottle]
  );

  return ignoreThrottle ? callback : throttledCallback; // Return the throttled callback
};

// Debounce a value
export function useMemoDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  const debouncedSetter = useMemo(() => debounce((val: T) => setDebouncedValue(val), delay), [delay]);

  useEffect(() => {
    debouncedSetter(value);
    return () => {
      debouncedSetter.cancel();
    };
  }, [value, debouncedSetter]);

  return debouncedValue;
}
